// import node module libraries
import React from 'react';
import { ListGroup, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Check } from 'react-feather';

// import custom components
import GKOdometer from 'components/elements/odometer/GKOdometer';
import { useSelector } from 'react-redux';

// import authentication components
import useIsAuthenticated from 'react-auth-kit/hooks/useIsAuthenticated';

// import translation hook
import { useTranslation } from 'react-i18next';

const PlanPricingCard = ({ content, interval = 'month' }) => {
	const { t } = useTranslation(); // useTranslation-Hook
	let plan = content;

	// Check if user is logged in
	const isAuthenticated = useIsAuthenticated();

	const PlanPricing = (amount, currency, intervalType) => {
		if (amount === 0) {
			return <h1 className="mb-3 fw-bold mt-5">{t('Free of charge')}</h1>;
		} else {
			// Convert amount from cents to euros
			let monthlyPrice;

			if (intervalType === 'year') {
				// Calculate monthly price from yearly amount
				monthlyPrice = ((amount / 100) / 12);
			} else {
				// Monthly amount
				monthlyPrice = (amount / 100);
			}

			return (
				<h1 className="mb-3 fw-bold mt-5">
					<GKOdometer value={monthlyPrice} suffix="€" />
					<span className="fs-5 text-muted fw-normal">€/{t('Month')}</span>
					{intervalType === 'year' && (
						<div className="fs-6 text-muted fw-normal">
							({t('billed annually')}: {(amount / 100).toFixed(0).replace('.', ',')}€)
						</div>
					)}
				</h1>
			);
		}
	};

	const getPlanTitle = (productName) => {
		const titles = {
			'haeppy.platform.free': 'platform.free',
			'haeppy.platform.starter': 'platform.starter',
			'haeppy.platform.professional': 'platform.professional',
			'haeppy.platform.enterprise': 'platform.enterprise'
		};
		return titles[productName] || productName;
	};

	const getBorderColor = (productName) => {
		const colors = {
			'haeppy.platform.free': 'secondary',
			'haeppy.platform.starter': 'primary',
			'haeppy.platform.professional': 'warning',
			'haeppy.platform.enterprise': 'success'
		};
		return colors[productName] || 'primary';
	};

	const getButtonClass = (productName) => {
		const classes = {
			'haeppy.platform.free': 'outline-primary',
			'haeppy.platform.starter': 'primary',
			'haeppy.platform.professional': 'warning',
			'haeppy.platform.enterprise': 'success'
		};
		return classes[productName] || 'outline-primary';
	};

	const getButtonText = (productName, amount) => {
		if (amount === 0) {
			return isAuthenticated ? t('Choose Plan') : t('Register now for free');
		}
		if (productName === 'haeppy.platform.enterprise') {
			return t('Contact us for pricing');
		}
		return isAuthenticated ? t('Choose Plan') : t('Start free trial (14 days)');
	};

	const getButtonLink = (plan) => {
		const links = {
			'haeppy.platform.free': isAuthenticated ? `/marketing/pages/checkout` : '/authentication/sign-up',
			'haeppy.platform.starter': isAuthenticated ? `/marketing/pages/checkout` : '/authentication/sign-up',
			'haeppy.platform.professional': isAuthenticated ? `/marketing/pages/checkout` : '/authentication/sign-up',
			'haeppy.platform.enterprise': isAuthenticated ? '/marketing/help-center/support' : '/marketing/pages/contact'
		};
		return links[plan.product_name] || '/authentication/sign-up';
	};

	return (
		<Card className="mb-3 border shadow-none border-top-0 rounded border-0">
			<div
				className={`border-top border-6 rounded border-${getBorderColor(plan.product_name)}`}
			>
				{/* Card body */}
				<div className="p-5">
					<div className="mb-5">
						<h3 className="fw-bold">{getPlanTitle(plan.product_name)}</h3>
						{plan.product_description && (
							<p
								className="mb-0"
								dangerouslySetInnerHTML={{ __html: plan.product_description }}
							></p>
						)}
						{PlanPricing(plan.amount, plan.currency, plan.interval)}
						<Link
							to={getButtonLink(plan)}
							state={{ plan: plan }} // Produkt über state übergeben
							className={`btn btn-${getButtonClass(plan.product_name)}`}
						>
							{getButtonText(plan.product_name, plan.amount)}
						</Link>
					</div>
					<hr className="m-0" />
					<div className="mt-5">
						<h4 className="fw-bold mb-4">Features</h4>
						{/* List of features */}
						<ListGroup bsPrefix="list-unstyled ">
							{plan.marketing_features?.map((item, index) => {
								return (
									<ListGroup.Item
										key={index}
										className="mb-1"
										bsPrefix="list-item"
									>
										<span className="text-success me-2">
											<Check size={16} />
										</span>
										<span>{item.name}</span>
									</ListGroup.Item>
								);
							})}
						</ListGroup>
					</div>
				</div>
			</div>
		</Card>
	);
};

export default PlanPricingCard;